import { getLocaleDate } from "@/constants";
import { useAppSelector } from "@/redux/hooks";
import { Card, CardContent, CardFooter } from "@/shadcn/ui/card";
import { Badge } from "@/shadcn/ui/badge";
import { Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import {
    BookOpen,
    Users,
    Calendar,
    Video,
    Tag,
    Sparkles,
    Clock,
    MonitorPlay,
} from "lucide-react";

export default function ({ element }: any) {
    const { t } = useTranslation();
    const { currentLang }: any = usePage().props;
    const { dir, isRTL } = useAppSelector((state) => state.locale);

    // Group badge configuration
    const getGroupBadgeConfig = (group: string) => {
        switch (group) {
            case "online":
                return {
                    icon: Video,
                    bgColor: "bg-teal-600",
                    textColor: "text-white",
                };
            case "recorded":
                return {
                    icon: MonitorPlay,
                    bgColor: "bg-gray-700",
                    textColor: "text-white",
                };
            case "attendance":
                return {
                    icon: Users,
                    bgColor: "bg-teal-700",
                    textColor: "text-white",
                };
            default:
                return {
                    icon: Users,
                    bgColor: "bg-gray-600",
                    textColor: "text-white",
                };
        }
    };

    const badgeConfig = getGroupBadgeConfig(element.group);
    const BadgeIcon = badgeConfig.icon;

    return (
        <Card
            dir={dir}
            className="group overflow-hidden border-gray-200 hover:border-teal-300 hover:shadow-2xl transition-all duration-300 bg-white h-full flex flex-col"
        >
            <Link
                href={`${route(`${currentLang}.frontend.course.show`, element.id)}?slug=${element.name}`}
                className="relative block overflow-hidden"
            >
                {/* Image Container */}
                <div className="relative h-40 sm:h-56 overflow-hidden bg-gray-100">
                    {/* Blurred backdrop */}
                    <img
                        src={element.thumb}
                        className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl"
                        alt=""
                        aria-hidden="true"
                    />
                    {/* Main image */}
                    <img
                        src={element.thumb}
                        className="relative w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                        alt={element.name}
                        loading="lazy"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    {/* Sale Badge */}
                    {element.on_sale && element.sale_price >= 0 && (
                        <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
                            <Badge className="bg-red-600 text-white border-0 shadow-lg flex items-center gap-1 sm:gap-2 px-2 py-1 sm:px-3 sm:py-1.5">
                                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="text-[10px] sm:text-xs font-semibold">
                                    {t("on_sale")}
                                </span>
                            </Badge>
                        </div>
                    )}

                    {/* Group Badge */}
                    <div className="absolute top-2 left-2 sm:top-3 sm:left-3">
                        <Badge
                            className={`${badgeConfig.bgColor} ${badgeConfig.textColor} border-0 shadow-lg flex items-center gap-1 sm:gap-2 px-2 py-1 sm:px-3 sm:py-1.5`}
                        >
                            <BadgeIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span className="text-[10px] sm:text-xs font-semibold">
                                {t(element.group)}
                            </span>
                        </Badge>
                    </div>
                </div>
            </Link>

            <CardContent className="p-3 sm:p-5 flex-1 flex flex-col">
                <Link
                    href={`${route(`${currentLang}.frontend.course.show`, element.id)}?slug=${element.name}`}
                    className="flex-1 flex flex-col"
                >
                    {/* Course Title */}
                    <h3 className="text-sm sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3 line-clamp-2 group-hover:text-teal-600 transition-colors">
                        {element.name}
                    </h3>

                    {/* Teacher Info */}
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-4">
                        <img
                            src={element.teacher.thumb}
                            className="w-5 h-5 sm:w-8 sm:h-8 rounded-full border border-gray-200 sm:border-2 object-cover"
                            alt={element.teacher.name}
                            loading="lazy"
                        />
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] sm:text-sm font-medium text-gray-700 truncate">
                                {`${element.teacher.title.abbreviation} ${element.teacher.name}`}
                            </p>
                        </div>
                    </div>

                    {/* Course Details */}
                    <div className="grid grid-cols-2 gap-1 sm:gap-2 mb-2 sm:mb-4">
                        <div className="flex items-center gap-1 sm:gap-2 text-gray-600">
                            <BadgeIcon className="w-3 h-3 sm:w-4 sm:h-4 text-teal-600 flex-shrink-0" />
                            <span className="text-[10px] sm:text-xs font-medium truncate">
                                {t(element.group)}
                            </span>
                        </div>

                        <div className="flex items-center gap-1 sm:gap-2 text-gray-600">
                            <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-teal-600 flex-shrink-0" />
                            <span className="text-[7px] sm:text-xs font-medium">
                                {`${element.sessions.length} ${t("sessions")}`}
                            </span>
                        </div>

                        {element.group === "attendance" &&
                            element.start_date && (
                                <div className="flex items-center gap-1 sm:gap-2 text-gray-600 col-span-2">
                                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-teal-600 flex-shrink-0" />
                                    <span className="text-[10px] sm:text-xs font-medium">
                                        {getLocaleDate(
                                            element.start_date,
                                            isRTL,
                                        )}
                                    </span>
                                </div>
                            )}
                    </div>

                    {/* Subject Info Badges - hidden on small screens */}
                    <div className="hidden sm:flex flex-wrap gap-2">
                        <Badge
                            variant="secondary"
                            className="bg-gray-100 text-gray-700 text-[9px] sm:text-xs px-1.5 py-0.5 sm:px-2.5 sm:py-0.5"
                        >
                            <Tag className="w-2.5 h-2.5 sm:w-3 sm:h-3 me-0.5 sm:me-1" />
                            {element.subject.name}
                        </Badge>
                        <Badge
                            variant="secondary"
                            className="bg-gray-100 text-gray-700 text-[9px] sm:text-xs px-1.5 py-0.5 sm:px-2.5 sm:py-0.5"
                        >
                            {element.subject.grade.stage.name}
                        </Badge>
                        <Badge
                            variant="secondary"
                            className="bg-gray-100 text-gray-700 text-[9px] sm:text-xs px-1.5 py-0.5 sm:px-2.5 sm:py-0.5"
                        >
                            {element.subject.grade.name}
                        </Badge>
                    </div>
                </Link>
            </CardContent>

            <CardFooter className="p-3 sm:p-5 pt-0">
                <Link
                    href={`${route(`${currentLang}.frontend.course.show`, element.id)}?slug=${element.name}`}
                    className="w-full"
                >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                        {/* Price Section */}
                        <div className="flex flex-col">
                            {element.on_sale && element.sale_price >= 0 ? (
                                <>
                                    {element.sale_price == 0 ? (
                                        <div className="text-lg sm:text-2xl font-bold text-teal-600">
                                            {t("free")}
                                        </div>
                                    ) : (
                                        <div className="flex items-baseline gap-1 sm:gap-2">
                                            <div className="text-lg sm:text-2xl font-bold text-teal-600">
                                                {element.sale_price}
                                                <span className="text-[10px] sm:text-sm font-medium ms-0.5 sm:ms-1">
                                                    {t("kd")}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    <div className="text-[10px] sm:text-sm text-gray-500 line-through">
                                        {element.total_price} {t("kd")}
                                    </div>
                                </>
                            ) : (
                                <div className="text-lg sm:text-2xl font-bold text-teal-600">
                                    {element.total_price}
                                    <span className="text-[10px] sm:text-sm font-medium ms-0.5 sm:ms-1">
                                        {t("kd")}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* View Button */}
                        <div className="flex items-center gap-1 sm:gap-2 px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg bg-teal-600 text-white group-hover:bg-teal-700 transition-colors font-medium text-[10px] sm:text-sm shadow-md w-full sm:w-auto justify-center">
                            <span>{t("view_details")}</span>
                            <BookOpen className="w-3 h-3 sm:w-4 sm:h-4" />
                        </div>
                    </div>
                </Link>
            </CardFooter>
        </Card>
    );
}
